import { useGetReturnsIntakeByDriverQuery } from '../services/returnsIntake.endpoints';
import { ShippingWithReturnIntakeSummaryDto } from '../models';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';

export const COMPLIANCE_MAP: Record<string, string> = {
  RETURNED: 'returned',
  RETURNED_CANCELLED: 'returned',
  UNEXPECTED_RETURN: 'unexpected',
  UNEXPECTED_RETURN_DELIVERED: 'unexpected',
  NOT_REPORTED: 'nonReported',
};

export const useReturnsIntakeData = () => {
  const { driverId } = useParams<{ driverId: string }>();
  const [searchParams] = useSearchParams();
  const closedAt = searchParams.get('closedAt') ?? undefined;

  const { data, isLoading, isError, refetch } = useGetReturnsIntakeByDriverQuery(
    { driverId: driverId || '', closedAt },
    { skip: !driverId }
  );

  const stats = useMemo(() => {
    const counts = { returned: 0, unexpected: 0, nonReported: 0 };
    data?.complianceSummary?.forEach(({ complianceName, count }) => {
      const key = COMPLIANCE_MAP[complianceName] as keyof typeof counts;
      if (key) counts[key] += count;
    });
    return counts;
  }, [data]);

  const mappedShippings = useMemo((): ShippingWithReturnIntakeSummaryDto[] => {
    return (data?.shippings || []).map((item) => ({
      id: String(item.shippingNumber),
      shippingNumber: item.shippingNumber,
      isReturned: item.complianceName === 'RETURNED' || item.complianceName === 'RETURNED_CANCELLED',
      complianceName: item.complianceName,
      complianceCommercialName: item.complianceCommercialName,
      complianceCommercialExplanation: '',
      scannedAt: item.createdAt,
      createdAt: item.createdAt,
      updatedAt: item.createdAt,
      deliveryAt: null,
      slaLimit: null,
      returnsIntakeStatus: data?.status || '',
      driverId: driverId || '',
      driverName: data?.driverName || '',
      routeId: '',
      routeName: '',
      recipient: item.recipient,
      recipientPhoneNumber: '',
      commerceShippingNumber: item.commerceShippingNumber || '-',
      shippingStatus: item.statusName,
      shippingStatusCommercialName: item.statusCommercialName || item.statusName,
      commerceName: item.commerceName,
      logisticOperatorName: '',
      commercialService: '',
      destinationAddress: {
        streetName: item.address || '',
        doorNumber: '',
        apartmentNumber: null,
        corner: null,
      },
    }));
  }, [data, driverId]);

  return {
    data,
    driverId,
    isLoading,
    isError,
    refetch,
    stats,
    mappedShippings,
  };
};

export default useReturnsIntakeData;
